// Operator kill switches — disable a channel, or everything, for an organization.
//
// MASTER_ADMIN only. Deliberately narrower than the usual admin gate: SUPER_ADMIN
// is granted per service organization, so a reseller's own super admin would
// otherwise be able to lift the suspension placed on that reseller. The backend
// enforces this too — the check here is so someone who types the URL gets a
// straight answer instead of a wall of failed requests.
//
// The design principle for this page is show-before-you-cut: a cascaded lock can
// take a whole client base off the air, so the affected organizations are listed
// by name before the switch can be thrown.
import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldOff, Plus, Loader2, AlertTriangle, Check, X, Building2, RotateCcw, Search, Info,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import {
  featureLocksService, FeatureLock, LockedFeature, LockScope, LockMode, LockCheckResult,
} from 'api/services/featureLocks.service';
import { organizationsService, Organization } from 'api/services/organizations.service';

const FEATURES: LockedFeature[] = ['ALL', 'WHATSAPP', 'VOICE', 'SMS', 'RCS', 'EMAIL'];

const BLANK = {
  organizationId: '',
  feature: 'WHATSAPP' as LockedFeature,
  scope: 'ORG_ONLY' as LockScope,
  mode: 'BLOCK_SEND' as LockMode,
  blockInbound: false,
  reason: '',
};

const FeatureLocks: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = ((currentOrganization as any)?.role || (user as any)?.role || '').toLowerCase();
  const isMasterAdmin = role === 'master_admin';

  const [locks, setLocks] = useState<FeatureLock[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<typeof BLANK | null>(null);
  const [saving, setSaving] = useState(false);
  const [lifting, setLifting] = useState<string | null>(null);

  // Cascade checker — the only way to confirm a parent-scoped lock reaches the
  // client you meant without waiting for a customer to complain.
  const [checkOrg, setCheckOrg] = useState('');
  const [checkFeature, setCheckFeature] = useState<LockedFeature>('WHATSAPP');
  const [checkResult, setCheckResult] = useState<LockCheckResult | null>(null);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [l, services, businesses] = await Promise.all([
      featureLocksService.list(),
      organizationsService.getServiceProviders().catch(() => ({ success: false, data: [] } as any)),
      organizationsService.getAllBusinesses().catch(() => ({ success: false, data: [] } as any)),
    ]);
    if (l.success) setLocks(l.data || []);
    else setError(l.error || 'Failed to load locks');

    // Both lists together give the whole tree, which is what the cascade preview
    // needs — a service org can hold both child services and businesses.
    const all = [...((services as any).data || []), ...((businesses as any).data || [])];
    const byId = new Map<string, Organization>();
    all.forEach(o => o?.id && byId.set(o.id, o));
    setOrgs(Array.from(byId.values()));
    setLoading(false);
  };

  useEffect(() => { if (isMasterAdmin) load(); else setLoading(false); /* eslint-disable-next-line */ }, [isMasterAdmin]);

  const orgName = (id?: string) => (id && orgs.find(o => o.id === id)?.name) || id || '—';

  /** Every organization beneath `rootId`, at any depth. Mirrors the server walk. */
  const descendantsOf = (rootId: string): Organization[] => {
    const out: Organization[] = [];
    const seen = new Set<string>([rootId]);
    let frontier = [rootId];
    while (frontier.length) {
      const next: string[] = [];
      for (const o of orgs) {
        if (o.parentOrganizationId && frontier.includes(o.parentOrganizationId) && !seen.has(o.id)) {
          seen.add(o.id);
          out.push(o);
          next.push(o.id);
        }
      }
      frontier = next;
    }
    return out;
  };

  // What the pending lock would actually take out. Shown before it can be saved.
  const impact = useMemo(() => {
    if (!draft?.organizationId) return null;
    const self = orgs.find(o => o.id === draft.organizationId);
    const kids = draft.scope === 'ORG_AND_DESCENDANTS' ? descendantsOf(draft.organizationId) : [];
    return { self, kids };
    /* eslint-disable-next-line */
  }, [draft?.organizationId, draft?.scope, orgs]);

  const save = async () => {
    if (!draft?.organizationId) return;
    setSaving(true);
    setError(null);
    const r = await featureLocksService.create(draft);
    setSaving(false);
    if (r.success) { setDraft(null); load(); } else setError(r.error || 'Failed to set the lock');
  };

  const lift = async (lock: FeatureLock) => {
    if (!lock.id) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `Lift the ${lock.feature} lock on ${orgName(lock.organizationId)}? Service resumes immediately.`
    )) return;
    setLifting(lock.id);
    const r = await featureLocksService.lift(lock.id);
    setLifting(null);
    if (r.success) load(); else setError(r.error || 'Failed to lift the lock');
  };

  const runCheck = async () => {
    if (!checkOrg) return;
    setChecking(true);
    setCheckResult(null);
    const r = await featureLocksService.check(checkOrg, checkFeature, 'SEND');
    setChecking(false);
    if (r.success) setCheckResult(r.data || null); else setError(r.error || 'Check failed');
  };

  if (!isMasterAdmin) {
    return (
      <div className="tl-card text-center py-12">
        <ShieldOff className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm font-medium text-gray-900 dark:text-white">Master admin only</p>
        <p className="text-[13px] mt-1 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
          Suspending an organization is a platform-level action. It is restricted to master admins so that
          a provider's own administrators cannot lift a suspension placed on them.
        </p>
      </div>
    );
  }

  const active = locks.filter(l => l.active);
  const history = locks.filter(l => !l.active);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldOff className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Service controls
          </h1>
          <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
            Disable a channel — or everything — for an organization. A lock set on a provider can cascade to
            every client beneath it, including ones onboarded later.
          </p>
        </div>
        <button className="tl-btn tl-btn-primary" onClick={() => setDraft({ ...BLANK })}>
          <Plus className="w-4 h-4" /> Disable a service
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">{error}</div>}

      {/* ── Active suspensions ──────────────────────────────────────────────── */}
      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : active.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Check className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--success-text)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">Nothing is suspended</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            All organizations have full access to every channel.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {active.map(l => {
            const kids = l.scope === 'ORG_AND_DESCENDANTS' ? descendantsOf(l.organizationId) : [];
            return (
              <div key={l.id} className="tl-card" style={{ borderLeft: '3px solid var(--danger-text)' }}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white">{orgName(l.organizationId)}</span>
                      <span className="tl-pill tl-pill-danger">{l.feature === 'ALL' ? 'ALL SERVICES' : l.feature}</span>
                      <span className="tl-pill">{l.mode === 'BLOCK_ALL' ? 'Fully hidden' : 'Sending blocked'}</span>
                      {l.scope === 'ORG_AND_DESCENDANTS' && (
                        <span className="tl-pill tl-pill-amber">
                          <Building2 className="w-3 h-3" /> + {kids.length} client{kids.length === 1 ? '' : 's'}
                        </span>
                      )}
                      {l.blockInbound && <span className="tl-pill tl-pill-danger">Inbound dropped</span>}
                    </div>
                    {l.reason && (
                      <div className="text-[13px] mt-1.5 text-gray-900 dark:text-white">{l.reason}</div>
                    )}
                    <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      set {l.createdAt ? new Date(l.createdAt).toLocaleString() : '—'}
                      {l.createdBy ? ` by ${l.createdBy}` : ''}
                    </div>
                    {kids.length > 0 && (
                      <div className="text-[12px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                        Also affects: {kids.map(k => k.name).join(', ')}
                      </div>
                    )}
                    {l.blockInbound && (
                      <div className="text-[12px] mt-1.5 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5"
                           style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          Incoming messages are being discarded, not queued. They cannot be recovered after
                          the lock is lifted.
                        </span>
                      </div>
                    )}
                  </div>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm flex-shrink-0"
                          onClick={() => lift(l)} disabled={lifting === l.id}>
                    {lifting === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Lift
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Cascade checker ─────────────────────────────────────────────────── */}
      {!loading && (
        <div className="tl-card">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span className="font-semibold text-gray-900 dark:text-white text-sm">Check an organization</span>
          </div>
          <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
            Resolves the cascade for one organization, so you can confirm a provider-level lock reaches the
            client you intended — rather than finding out from a support ticket.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="min-w-[220px]">
              <label className="tl-label">Organization</label>
              <select className="tl-input" value={checkOrg} onChange={e => setCheckOrg(e.target.value)}>
                <option value="">Select…</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="tl-label">Channel</label>
              <select className="tl-input" value={checkFeature}
                      onChange={e => setCheckFeature(e.target.value as LockedFeature)}>
                {FEATURES.filter(f => f !== 'ALL').map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <button className="tl-btn tl-btn-secondary" onClick={runCheck} disabled={!checkOrg || checking}>
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Check
            </button>
          </div>
          {checkResult && (
            <div className="mt-3 text-[13px] px-3 py-2 rounded-lg flex items-start gap-2"
                 style={{
                   background: checkResult.blocked ? 'var(--amber-tint)' : 'var(--success-tint, rgba(16,185,129,.08))',
                   color: checkResult.blocked ? 'var(--amber-label)' : 'var(--success-text)',
                 }}>
              {checkResult.blocked ? <X className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span>
                {checkResult.blocked ? (
                  <>
                    <strong>Blocked.</strong> {checkFeature} sending is disabled for {orgName(checkOrg)}
                    {checkResult.lockedOnOrganizationId && checkResult.lockedOnOrganizationId !== checkOrg && (
                      <> — inherited from a lock on <strong>{orgName(checkResult.lockedOnOrganizationId)}</strong></>
                    )}
                    {checkResult.reason ? `. Reason: ${checkResult.reason}` : '.'}
                  </>
                ) : (
                  <><strong>Allowed.</strong> {checkFeature} sending works normally for {orgName(checkOrg)}.</>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Lift history ────────────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="tl-card">
          <div className="font-semibold text-gray-900 dark:text-white text-sm mb-2">Previously lifted</div>
          <div className="space-y-1.5">
            {history.map(l => (
              <div key={l.id} className="text-[12.5px] flex items-center gap-2 flex-wrap"
                   style={{ color: 'var(--text-muted)' }}>
                <span className="tl-pill">{l.feature}</span>
                <span>{orgName(l.organizationId)}</span>
                <span>·</span>
                <span>lifted {l.liftedAt ? new Date(l.liftedAt).toLocaleString() : '—'}{l.liftedBy ? ` by ${l.liftedBy}` : ''}</span>
                {l.reason && <span className="italic">“{l.reason}”</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Create dialog ───────────────────────────────────────────────────── */}
      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !saving && setDraft(null)}>
          <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Disable a service</h2>

            <div className="space-y-3">
              <div>
                <label className="tl-label">Organization</label>
                <select className="tl-input" value={draft.organizationId}
                        onChange={e => setDraft({ ...draft, organizationId: e.target.value })}>
                  <option value="">Select…</option>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.type === 'BUSINESS' ? ' (client)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="tl-label">What to disable</label>
                  <select className="tl-input" value={draft.feature}
                          onChange={e => setDraft({ ...draft, feature: e.target.value as LockedFeature })}>
                    {FEATURES.map(f => <option key={f} value={f}>{f === 'ALL' ? 'Everything' : f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="tl-label">Reach</label>
                  <select className="tl-input" value={draft.scope}
                          onChange={e => setDraft({ ...draft, scope: e.target.value as LockScope })}>
                    <option value="ORG_ONLY">This organization only</option>
                    <option value="ORG_AND_DESCENDANTS">This organization and all its clients</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="tl-label">How hard</label>
                <select className="tl-input" value={draft.mode}
                        onChange={e => setDraft({ ...draft, mode: e.target.value as LockMode })}>
                  <option value="BLOCK_SEND">Block sending — history and reports stay readable</option>
                  <option value="BLOCK_ALL">Hide entirely — reads blocked too</option>
                </select>
                <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {draft.mode === 'BLOCK_SEND'
                    ? 'Recommended for billing holds and abuse review: outbound stops, but the customer keeps their history and support can still investigate.'
                    : 'The section goes dark for the customer. Support loses visibility too, and it reads like an outage rather than a suspension.'}
                </p>
              </div>

              <div>
                <label className="tl-label">Reason</label>
                <input className="tl-input" value={draft.reason} placeholder="e.g. Billing hold — invoice 4821 overdue"
                       onChange={e => setDraft({ ...draft, reason: e.target.value })} />
                <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Shown to the blocked caller and recorded against the lock, so support has a straight answer.
                </p>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={draft.blockInbound}
                       onChange={e => setDraft({ ...draft, blockInbound: e.target.checked })} />
                <span className="text-[13px] text-gray-900 dark:text-white">
                  Also drop incoming messages
                  <span className="block text-[12px]" style={{ color: 'var(--text-muted)' }}>
                    Off by default. Messages customers send during the suspension are discarded, not queued —
                    they cannot be re-fetched from Meta afterwards.
                  </span>
                </span>
              </label>

              {/* Show-before-you-cut: name the organizations that go dark. */}
              {impact?.self && (
                <div className="text-[12.5px] px-3 py-2 rounded-lg"
                     style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>
                        {draft.feature === 'ALL' ? 'All services' : draft.feature} will stop for{' '}
                        {1 + (impact.kids?.length || 0)} organization
                        {1 + (impact.kids?.length || 0) === 1 ? '' : 's'}:
                      </strong>
                      <div className="mt-1">
                        {impact.self.name}
                        {impact.kids && impact.kids.length > 0 && `, ${impact.kids.map(k => k.name).join(', ')}`}
                      </div>
                      {draft.scope === 'ORG_ONLY' && (impact.self as any) && descendantsOf(draft.organizationId).length > 0 && (
                        <div className="mt-1.5 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>
                            Its {descendantsOf(draft.organizationId).length} client
                            {descendantsOf(draft.organizationId).length === 1 ? '' : 's'} will keep working.
                            Switch Reach to include them.
                          </span>
                        </div>
                      )}
                      {/* A running campaign pauses rather than failing its recipients. */}
                      {(draft.feature === 'ALL' || draft.feature === 'WHATSAPP') && (
                        <div className="mt-1.5">
                          Any WhatsApp campaign running right now will pause; its remaining recipients stay
                          pending and resume when the lock is lifted.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button className="tl-btn tl-btn-secondary" onClick={() => setDraft(null)} disabled={saving}>Cancel</button>
              <button className="tl-btn tl-btn-danger" onClick={save} disabled={saving || !draft.organizationId}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                Disable now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureLocks;
